import type { DataFileListItem, TelemetrySessionContext, TrackContribution } from '@kangaroo/shared';

import { fetchDataFileContent } from '../features/dataFiles';
import { fetchTelemetryTracks, updateTelemetrySessionContext } from '../features/telemetryContext';
import { listAssignableCircuitContributions } from './circuitMapOverlay';
import {
  buildDataFileSessionSubtitle,
  buildDataFileSessionTitle,
} from './telemetrySessions';
import { isMyChronSessionExport, type MyChronSessionExport } from './telemetrySession';
import { loadSessionTrackBinding } from './resolveSessionTrackBinding';
import {
  buildContributionDraftPayload,
  matchSessionToCircuitDraft,
  recalibrateTrackGeometry,
  runEnrichmentPipeline,
  type CircuitDraftMatch,
} from './trackEnrichment';
import {
  createTrackContributionDraftId,
  fetchTrackContributionsByIds,
  mergeContributionDetails,
  saveTrackContributionDraft,
} from './trackContributions';
import { trackContributionHasGeometry } from '@kangaroo/shared';

import {
  clearDismissedCircuitDiscoveries,
  dismissCircuitDiscoveryKey,
  dismissCircuitDiscoveryLocationKey,
  dismissCircuitDiscoveryVenueKey,
  getDismissedCircuitDiscoveryKeys,
  getDismissedCircuitDiscoveryLocationKeys,
  getDismissedCircuitDiscoveryVenueKeys,
  restoreCircuitDiscoveryKey,
  restoreCircuitDiscoveryLocationKey,
  restoreCircuitDiscoveryVenueKey,
} from './circuitDiscoveryCache';

const DISCOVERY_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_VISIBLE_DISCOVERIES = 5;

export type CircuitDiscoveryCandidate = {
  sessionKey: string;
  title: string;
  subtitle: string;
  venueLabel: string;
  dataFile: DataFileListItem;
};

export type CircuitDiscoveryOpenResult =
  | { kind: 'created'; contributionId: string; trackId: string }
  | { kind: 'matched'; contributionId: string; trackId: string; circuitName: string }
  | { kind: 'failed'; message: string };

export type DismissedCircuitDiscoveryState = {
  sessionKeys: Set<string>;
  venueKeys: Set<string>;
  locationKeys: Set<string>;
};

export function normalizeCircuitDiscoveryVenueKey(label: string) {
  return label.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function getDismissedCircuitDiscoveries(): DismissedCircuitDiscoveryState {
  return {
    sessionKeys: getDismissedCircuitDiscoveryKeys(),
    venueKeys: getDismissedCircuitDiscoveryVenueKeys(),
    locationKeys: getDismissedCircuitDiscoveryLocationKeys(),
  };
}

export function dismissCircuitDiscovery(sessionKey: string) {
  dismissCircuitDiscoveryKey(sessionKey);
}

export function dismissCircuitDiscoveryCandidate(candidate: CircuitDiscoveryCandidate) {
  dismissCircuitDiscoveryKey(candidate.sessionKey);
  dismissCircuitDiscoveryVenueKey(normalizeCircuitDiscoveryVenueKey(candidate.venueLabel));
  const locationKey = discoveryLocationKey(candidate.dataFile);
  if (locationKey) {
    dismissCircuitDiscoveryLocationKey(locationKey);
  }
}

export function restoreCircuitDiscoveryCandidate(candidate: CircuitDiscoveryCandidate) {
  restoreCircuitDiscoveryKey(candidate.sessionKey);
  restoreCircuitDiscoveryVenueKey(normalizeCircuitDiscoveryVenueKey(candidate.venueLabel));
  const locationKey = discoveryLocationKey(candidate.dataFile);
  if (locationKey) {
    restoreCircuitDiscoveryLocationKey(locationKey);
  }
}

export function restoreCircuitDiscovery(sessionKey: string) {
  restoreCircuitDiscoveryKey(sessionKey);
}

export function resetDismissedCircuitDiscoveries(userId?: string | null) {
  clearDismissedCircuitDiscoveries(userId);
}

function hasSavedCircuitForSession(contributions: TrackContribution[], sessionKey: string) {
  return contributions.some((contribution) => contribution.sessionKey === sessionKey);
}

function hasGpsPreview(file: DataFileListItem) {
  return (file.mapPreview?.length ?? 0) >= 2;
}

function discoveryVenueKey(file: DataFileListItem) {
  return normalizeCircuitDiscoveryVenueKey(
    file.summary.trackName?.trim() || file.originalName.replace(/\.json$/i, ''),
  );
}

function discoveryLocationKey(file: DataFileListItem) {
  if (file.mapPreview.length < 2) {
    return null;
  }

  const totals = file.mapPreview.reduce(
    (current, point) => ({
      lat: current.lat + point.lat,
      lon: current.lon + point.lon,
    }),
    { lat: 0, lon: 0 },
  );
  const lat = totals.lat / file.mapPreview.length;
  const lon = totals.lon / file.mapPreview.length;

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return `gps:${lat.toFixed(3)}:${lon.toFixed(3)}`;
}

function isUnassignedDiscoveryFile(
  file: DataFileListItem,
  contributions: TrackContribution[],
  contextBySessionKey: Map<string, TelemetrySessionContext | null | undefined>,
  dismissed: DismissedCircuitDiscoveryState,
) {
  if (dismissed.sessionKeys.has(file.id)) {
    return false;
  }

  if (dismissed.venueKeys.has(discoveryVenueKey(file))) {
    return false;
  }

  const locationKey = discoveryLocationKey(file);
  if (locationKey && dismissed.locationKeys.has(locationKey)) {
    return false;
  }

  if (!hasGpsPreview(file)) {
    return false;
  }

  if (Date.now() - Date.parse(file.uploadedAt) > DISCOVERY_LOOKBACK_MS) {
    return false;
  }

  if (hasSavedCircuitForSession(contributions, file.id)) {
    return false;
  }

  const context = contextBySessionKey.get(file.id);
  if (context?.contributionId) {
    return false;
  }

  return true;
}

export function listRecentUnassignedDiscoveryFiles(
  dataFiles: DataFileListItem[],
  contributions: TrackContribution[],
  dismissed: DismissedCircuitDiscoveryState = getDismissedCircuitDiscoveries(),
) {
  return [...dataFiles]
    .filter((file) => {
      if (dismissed.sessionKeys.has(file.id)) {
        return false;
      }

      if (dismissed.venueKeys.has(discoveryVenueKey(file))) {
        return false;
      }

      const locationKey = discoveryLocationKey(file);
      if (locationKey && dismissed.locationKeys.has(locationKey)) {
        return false;
      }

      if (!hasGpsPreview(file)) {
        return false;
      }

      if (Date.now() - Date.parse(file.uploadedAt) > DISCOVERY_LOOKBACK_MS) {
        return false;
      }

      if (hasSavedCircuitForSession(contributions, file.id)) {
        return false;
      }

      return true;
    })
    .sort((left, right) => Date.parse(right.uploadedAt) - Date.parse(left.uploadedAt))
    .slice(0, MAX_VISIBLE_DISCOVERIES);
}

export function listUnassignedDiscoveryFiles(
  dataFiles: DataFileListItem[],
  contributions: TrackContribution[],
  contextBySessionKey: Map<string, TelemetrySessionContext | null | undefined>,
  dismissed: DismissedCircuitDiscoveryState = getDismissedCircuitDiscoveries(),
) {
  return [...dataFiles]
    .filter((file) => isUnassignedDiscoveryFile(file, contributions, contextBySessionKey, dismissed))
    .sort((left, right) => Date.parse(right.uploadedAt) - Date.parse(left.uploadedAt));
}

export function listCircuitDiscoveryCandidates(
  dataFiles: DataFileListItem[],
  contributions: TrackContribution[],
  contextBySessionKey: Map<string, TelemetrySessionContext | null | undefined>,
  dismissed: DismissedCircuitDiscoveryState = getDismissedCircuitDiscoveries(),
): CircuitDiscoveryCandidate[] {
  return listUnassignedDiscoveryFiles(dataFiles, contributions, contextBySessionKey, dismissed)
    .slice(0, MAX_VISIBLE_DISCOVERIES)
    .map((file) => ({
      sessionKey: file.id,
      title: buildDataFileSessionTitle(file),
      subtitle: buildDataFileSessionSubtitle(file),
      venueLabel: file.summary.trackName?.trim() || file.originalName.replace(/\.json$/i, ''),
      dataFile: file,
    }));
}

export function buildSessionExportFromDataFileListItem(
  file: DataFileListItem,
): MyChronSessionExport | null {
  if (file.mapPreview.length < 2 || file.laps.length === 0) {
    return null;
  }

  const session: MyChronSessionExport = {
    format: file.summary.format ?? 'mychron-json',
    channels: file.channels.map((channel, index) => ({
      index,
      shortName: channel.shortName,
      longName: channel.longName ?? channel.shortName,
      unit: channel.unit ?? '',
    })),
    laps: file.laps
      .filter((lap) => lap.durationMs !== null && lap.endTimeMs !== null)
      .map((lap) => ({
        segment: lap.segment ?? 0,
        lapNumber: lap.lapNumber,
        durationMs: lap.durationMs ?? 0,
        endTimeMs: lap.endTimeMs ?? 0,
      })),
    gps: file.mapPreview.map((point) => ({
      timecodeMs: point.timeMs,
      lat: point.lat,
      lon: point.lon,
      alt: 0,
      speed: (point.speedKph ?? 0) / 3.6,
    })),
    series: Object.fromEntries(
      file.previewSeries.map((series) => [
        series.shortName,
        series.points.map((point) => ({ timecodeMs: point.timeMs, value: point.value })),
      ]),
    ),
    metadata: {
      driver: file.summary.driverName ?? undefined,
      vehicle: file.summary.kartNumber ?? undefined,
      venue: file.summary.trackName ?? undefined,
    },
  };

  return isMyChronSessionExport(session) ? session : null;
}

async function loadSessionExport(dataFile: DataFileListItem): Promise<MyChronSessionExport> {
  const payload = await fetchDataFileContent(dataFile.id, 'full');
  if (!isMyChronSessionExport(payload)) {
    throw new Error('Session JSON is missing gps/laps/series data');
  }

  return payload;
}

function assignableCircuits(contributions: TrackContribution[]) {
  return listAssignableCircuitContributions(contributions);
}

async function assignableCircuitsWithGeometry(contributions: TrackContribution[]) {
  const assignable = assignableCircuits(contributions);
  const idsToHydrate = assignable
    .filter(
      (contribution) =>
        contribution.payload.gpsPolyline.length === 0
        && trackContributionHasGeometry(contribution.payload),
    )
    .map((contribution) => contribution.id);

  if (idsToHydrate.length === 0) {
    return assignable;
  }

  const hydrated = await fetchTrackContributionsByIds(idsToHydrate);
  const hydratedCatalog = mergeContributionDetails(contributions, ...hydrated);
  return assignableCircuits(hydratedCatalog);
}

export async function tryAssignMatchedCircuitForSession(input: {
  dataFile: DataFileListItem;
  contributions: TrackContribution[];
}): Promise<CircuitDraftMatch | null> {
  const sessionKey = input.dataFile.id;

  try {
    const session = buildSessionExportFromDataFileListItem(input.dataFile);
    if (!session) {
      return null;
    }

    const match = matchSessionToCircuitDraft(
      session,
      await assignableCircuitsWithGeometry(input.contributions),
    );
    if (!match) {
      return null;
    }

    await updateTelemetrySessionContext(sessionKey, {
      contributionId: match.draftId,
      trackId: match.trackId,
      dataFileId: input.dataFile.id,
    });

    return match;
  } catch {
    return null;
  }
}

export async function openCircuitDiscovery(input: {
  dataFile: DataFileListItem;
  contributions: TrackContribution[];
}): Promise<CircuitDiscoveryOpenResult> {
  const sessionKey = input.dataFile.id;

  try {
    const session = await loadSessionExport(input.dataFile);
    const assignableDrafts = await assignableCircuitsWithGeometry(input.contributions);

    const existingMatch = matchSessionToCircuitDraft(session, assignableDrafts);
    if (existingMatch) {
      await updateTelemetrySessionContext(sessionKey, {
        contributionId: existingMatch.draftId,
        trackId: existingMatch.trackId,
        dataFileId: input.dataFile.id,
      });

      return {
        kind: 'matched',
        contributionId: existingMatch.draftId,
        trackId: existingMatch.trackId,
        circuitName: existingMatch.circuitName,
      };
    }

    if (hasSavedCircuitForSession(input.contributions, sessionKey)) {
      const existing = input.contributions.find((contribution) => contribution.sessionKey === sessionKey);
      if (existing) {
        return {
          kind: 'created',
          contributionId: existing.id,
          trackId: existing.trackId,
        };
      }
    }

    const tracks = await fetchTelemetryTracks().catch(() => []);
    const trackBinding = await loadSessionTrackBinding(session, sessionKey, true);
    const pipeline = runEnrichmentPipeline(session, sessionKey, {
      savedTracks: trackBinding.tracks.length > 0 ? trackBinding.tracks : tracks,
      preferredTrackId: trackBinding.preferredTrackId,
    });

    if (!pipeline) {
      return { kind: 'failed', message: 'Could not analyze GPS shape for this session.' };
    }

    const trackId = pipeline.contribution.payload.trackId;
    const geometry = recalibrateTrackGeometry(pipeline.contribution.payload.gpsPolyline, 0);
    const payload = buildContributionDraftPayload(
      trackId,
      geometry,
      geometry.cornerApexSuggestions,
      pipeline.contribution.payload.confidenceScore,
    );
    const draftId = createTrackContributionDraftId();
    const venue =
      input.dataFile.summary.trackName?.trim()
      || pipeline.venue
      || pipeline.matchedTrack.name;

    const saved = await saveTrackContributionDraft(trackId, {
      sessionKey,
      draftId,
      venue,
      payload,
    });

    await updateTelemetrySessionContext(sessionKey, {
      dataFileId: input.dataFile.id,
      contributionId: saved.id,
      trackId,
    });

    return {
      kind: 'created',
      contributionId: saved.id,
      trackId,
    };
  } catch (error) {
    return {
      kind: 'failed',
      message: error instanceof Error ? error.message : 'Could not create circuit draft.',
    };
  }
}

export function buildCircuitDiscoveryEditorUrl(sessionKey: string, contributionId: string) {
  if (import.meta.env.DEV) {
    const params = new URLSearchParams({ session: sessionKey, draft: contributionId });
    return `/dev/track-enrichment?${params.toString()}`;
  }

  const params = new URLSearchParams({ circuit: contributionId, session: sessionKey });
  return `/sessions?${params.toString()}`;
}
